import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    UsersIcon,
    BanknotesIcon,
    AcademicCapIcon,
    CalendarIcon,
    ChartBarIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ShoppingCartIcon,
    HeartIcon,
    ArrowTrendingUpIcon,
    BookOpenIcon,
    PlusIcon,
    ClipboardDocumentListIcon,
    ArrowPathIcon,
    FunnelIcon
} from '@heroicons/react/24/outline';
import { apiGet } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import ActivityFeed from '../components/ActivityFeed';

interface DashboardData {
    cohorts: { count: number; totalFarmers: number; avgPlotSize: number; };
    vsla: { count: number; totalMembers: number; totalSavings: number; };
    training: { sessionsCount: number; totalAttendees: number; };
    upcomingTasks: Array<{ type: string; title: string; date: string; location: string; }>;
}

interface FieldHealth {
    cohort_id: number;
    name: string;
    location: string;
    farmer_count: number;
    yield_score: number;
    vsla_score: number;
    training_score: number;
    overall_health: number;
    cropping_system?: string;
}

interface FarmerStats {
    total: number;
    teen_mothers: number;
    female_headed: number;
    land_poor: number;
    champions: number;
    active: number;
}

interface TrainingStats {
    total_sessions: number;
    avg_attendance_rate: number;
    champions_trained: number;
    materials_count: number;
}

interface SalesStats {
    total_revenue: number;
    orders_count: number;
}

const FieldFacilitatorDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [data, setData] = useState<DashboardData | null>(null);
    const [fieldHealth, setFieldHealth] = useState<FieldHealth[]>([]);
    const [farmerStats, setFarmerStats] = useState<FarmerStats | null>(null);
    const [trainingStats, setTrainingStats] = useState<TrainingStats | null>(null);
    const [salesStats, setSalesStats] = useState<SalesStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [croppingFilter, setCroppingFilter] = useState<string>('all');

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setIsLoading(true);
        try {
            const [dashRes, healthRes, farmersRes, trainingRes, salesRes] = await Promise.allSettled([
                apiGet('/api/field-facilitator/dashboard') as Promise<any>,
                apiGet('/api/field-facilitator/cohort-health-matrix') as Promise<any>,
                apiGet('/api/farmers') as Promise<any>,
                apiGet('/api/agronomy/stats') as Promise<any>,
                apiGet('/api/marketplace/orders') as Promise<any>,
            ]);

            if (dashRes.status === 'fulfilled' && dashRes.value?.success) {
                setData(dashRes.value.data);
            }
            if (healthRes.status === 'fulfilled' && healthRes.value?.success) {
                setFieldHealth(healthRes.value.data);
            }
            if (farmersRes.status === 'fulfilled') {
                const farmers = Array.isArray(farmersRes.value)
                    ? farmersRes.value
                    : (Array.isArray((farmersRes.value as any)?.farmers) ? (farmersRes.value as any).farmers : []);
                setFarmerStats({
                    total: farmers.length,
                    teen_mothers: farmers.filter((f: any) => f.household_type === 'teen_mother').length,
                    female_headed: farmers.filter((f: any) => f.household_type === 'female_headed').length,
                    land_poor: farmers.filter((f: any) => f.household_type === 'land_poor').length,
                    champions: farmers.filter((f: any) => f.household_type === 'champion').length,
                    active: farmers.filter((f: any) => Boolean(f.is_active ?? f.status)).length,
                });
            }
            if (trainingRes.status === 'fulfilled') {
                const ts = trainingRes.value;
                setTrainingStats({
                    total_sessions: ts?.total_sessions || 0,
                    avg_attendance_rate: parseFloat(ts?.avg_attendance_rate || '0'),
                    champions_trained: ts?.champions_trained || 0,
                    materials_count: ts?.materials_count || 0,
                });
            }
            if (salesRes.status === 'fulfilled') {
                const orders = salesRes.value?.orders || [];
                setSalesStats({
                    total_revenue: orders.reduce((s: number, o: any) => s + parseFloat(o.total_amount || 0), 0),
                    orders_count: orders.length,
                });
            }
        } catch (error) {
            console.error('Error fetching FF dashboard data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getHealthColor = (score: number) => {
        if (score >= 85) return 'bg-green-500';
        if (score >= 75) return 'bg-yellow-400';
        if (score >= 50) return 'bg-orange-400';
        return 'bg-red-500';
    };

    const getHealthTextColor = (score: number) => {
        if (score >= 85) return 'text-green-700';
        if (score >= 75) return 'text-yellow-700';
        if (score >= 50) return 'text-orange-700';
        return 'text-red-700';
    };

    const getHealthBadge = (score: number) => {
        if (score >= 85) return { label: '✅ Active', bg: 'bg-green-100 text-green-800' };
        if (score >= 75) return { label: '⚠️ Warning', bg: 'bg-yellow-100 text-yellow-800' };
        return { label: '❗ At Risk', bg: 'bg-red-100 text-red-800' };
    };

    const filteredFields = croppingFilter === 'all'
        ? fieldHealth
        : fieldHealth.filter(c => c.cropping_system?.toLowerCase().includes(croppingFilter.toLowerCase()));

    const firstName = user?.full_name?.split(' ')[0] || 'Field Facilitator';

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto" />
                    <p className="mt-4 text-gray-500 text-sm">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">

            {/* ── Header ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-bold text-gray-900">
                            Muraho, {firstName}! 👋
                        </h1>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                            🌿 Field Facilitator
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                        Here's your field support overview for today, {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                </div>
                <button
                    onClick={fetchAllData}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <ArrowPathIcon className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* ── Quick Actions ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                    { label: 'Schedule Training', icon: CalendarIcon, color: 'bg-purple-600 hover:bg-purple-700', action: () => navigate('/dashboard/training') },
                    { label: 'VSLA Meeting', icon: BanknotesIcon, color: 'bg-green-600 hover:bg-green-700', action: () => navigate('/dashboard/vsla') },
                    { label: 'Register Farmer', icon: PlusIcon, color: 'bg-blue-600 hover:bg-blue-700', action: () => navigate('/dashboard/farmers') },
                    { label: 'Marketplace', icon: ShoppingCartIcon, color: 'bg-orange-500 hover:bg-orange-600', action: () => navigate('/marketplace/supplier') },
                    { label: 'Report Issue', icon: ExclamationTriangleIcon, color: 'bg-red-500 hover:bg-red-600', action: () => navigate('/dashboard/issues') },
                    { label: 'View Fields', icon: ClipboardDocumentListIcon, color: 'bg-indigo-500 hover:bg-indigo-600', action: () => navigate('/dashboard/farmers') },
                ].map((action) => {
                    const Icon = action.icon;
                    return (
                        <button
                            key={action.label}
                            onClick={action.action}
                            className={`${action.color} text-white rounded-xl p-4 flex flex-col items-center gap-2 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5`}
                        >
                            <Icon className="w-6 h-6" />
                            <span className="text-xs font-semibold text-center leading-tight">{action.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* ── KPI Section 1: Farmer Management ── */}
            <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">👨‍🌾 Farmer Management</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Total Farmers */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Total Farmers</p>
                            <div className="p-2 bg-blue-100 rounded-lg"><UsersIcon className="h-5 w-5 text-blue-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{farmerStats?.total ?? data?.cohorts.totalFarmers ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">tenant field network</p>
                        {farmerStats && (
                            <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-1 text-xs">
                                <span className="text-red-600">🔴 {farmerStats.teen_mothers} Teen Mothers</span>
                                <span className="text-yellow-600">🟡 {farmerStats.female_headed} Female-Headed</span>
                                <span className="text-blue-600">🔵 {farmerStats.land_poor} Land-Poor</span>
                                <span className="text-green-600">🟢 {farmerStats.champions} Champions</span>
                            </div>
                        )}
                    </div>

                    {/* Active Farmers */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Active Farmers</p>
                            <div className="p-2 bg-green-100 rounded-lg"><CheckCircleIcon className="h-5 w-5 text-green-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{farmerStats?.active ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">
                            {farmerStats && farmerStats.total > 0
                                ? `${Math.round((farmerStats.active / farmerStats.total) * 100)}% retention rate`
                                : 'No data'}
                        </p>
                    </div>

                    {/* Field Groups */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Field Groups</p>
                            <div className="p-2 bg-indigo-100 rounded-lg"><ClipboardDocumentListIcon className="h-5 w-5 text-indigo-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{data?.cohorts.count ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">Avg {(data?.cohorts.avgPlotSize ?? 0).toFixed(2)} ha/farmer</p>
                    </div>

                    {/* VSLA Groups */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">VSLA Groups</p>
                            <div className="p-2 bg-emerald-100 rounded-lg"><BanknotesIcon className="h-5 w-5 text-emerald-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{data?.vsla.count ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">{data?.vsla.totalMembers ?? 0} members</p>
                    </div>
                </div>
            </div>

            {/* ── KPI Section 2: VSLA Financial Health ── */}
            <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">💰 VSLA Financial Health</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Total Savings</p>
                            <div className="p-2 bg-green-100 rounded-lg"><BanknotesIcon className="h-5 w-5 text-green-600" /></div>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(data?.vsla.totalSavings ?? 0)}</p>
                        <p className="text-xs text-gray-500 mt-1">across all groups</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">VSLA Members</p>
                            <div className="p-2 bg-teal-100 rounded-lg"><HeartIcon className="h-5 w-5 text-teal-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{data?.vsla.totalMembers ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">active members</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                            <div className="p-2 bg-blue-100 rounded-lg"><ArrowTrendingUpIcon className="h-5 w-5 text-blue-600" /></div>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(salesStats?.total_revenue ?? 0)}</p>
                        <p className="text-xs text-gray-500 mt-1">{salesStats?.orders_count ?? 0} orders</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Avg Plot Size</p>
                            <div className="p-2 bg-orange-100 rounded-lg"><ClipboardDocumentListIcon className="h-5 w-5 text-orange-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{(data?.cohorts.avgPlotSize ?? 0).toFixed(2)}</p>
                        <p className="text-xs text-gray-500 mt-1">hectares avg</p>
                    </div>
                </div>
            </div>

            {/* ── KPI Section 3: Training & Capacity ── */}
            <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">📚 Training & Capacity Building</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Sessions Conducted</p>
                            <div className="p-2 bg-purple-100 rounded-lg"><AcademicCapIcon className="h-5 w-5 text-purple-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{trainingStats?.total_sessions ?? data?.training.sessionsCount ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">{data?.training.totalAttendees ?? 0} total attendees</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Attendance Rate</p>
                            <div className="p-2 bg-green-100 rounded-lg"><ChartBarIcon className="h-5 w-5 text-green-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">
                            {trainingStats ? `${Number(trainingStats.avg_attendance_rate).toFixed(0)}%` : '—'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">target ≥90%</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Champion Farmers</p>
                            <div className="p-2 bg-yellow-100 rounded-lg"><ClipboardDocumentListIcon className="h-5 w-5 text-yellow-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{trainingStats?.champions_trained ?? farmerStats?.champions ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">peer trainers certified</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-600">Materials</p>
                            <div className="p-2 bg-indigo-100 rounded-lg"><BookOpenIcon className="h-5 w-5 text-indigo-600" /></div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{trainingStats?.materials_count ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">in library</p>
                    </div>
                </div>
            </div>

            {/* ── Field Health Matrix ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Field Health Matrix</h2>
                        <p className="text-sm text-gray-500">Monitor farmer groups by crop, training, finance, and yield signals</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <FunnelIcon className="w-4 h-4 text-gray-400" />
                        <select
                            value={croppingFilter}
                            onChange={e => setCroppingFilter(e.target.value)}
                            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-brand-blue-500/20 focus:border-brand-blue-500"
                        >
                            <option value="all">All Cropping Systems</option>
                            <option value="avocado">Avocado</option>
                            <option value="macadamia">Macadamia</option>
                        </select>
                    </div>
                </div>

                {filteredFields.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No field groups found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3 pr-4">Field Group</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase pb-3 px-4">Farmers</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase pb-3 px-4">VSLA Health</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase pb-3 px-4">Training</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase pb-3 px-4">Yield</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase pb-3 pl-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredFields.map((cohort) => {
                                    const badge = getHealthBadge(cohort.overall_health);
                                    return (
                                        <tr
                                            key={cohort.cohort_id}
                                            onClick={() => navigate('/dashboard/farmers')}
                                            className="hover:bg-blue-50/50 cursor-pointer transition-colors group"
                                        >
                                            <td className="py-4 pr-4">
                                                <div>
                                                    <p className="font-semibold text-gray-900 group-hover:text-brand-blue-600 transition-colors">{cohort.name}</p>
                                                    <p className="text-xs text-gray-400">{cohort.cropping_system || cohort.location || '—'}</p>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <span className="text-sm font-medium text-gray-700">{cohort.farmer_count}</span>
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    <span className={`text-sm font-bold ${getHealthTextColor(cohort.vsla_score)}`}>{cohort.vsla_score.toFixed(0)}%</span>
                                                    <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                                        <div className={`h-1.5 rounded-full ${getHealthColor(cohort.vsla_score)}`} style={{ width: `${Math.min(cohort.vsla_score, 100)}%` }} />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    <span className={`text-sm font-bold ${getHealthTextColor(cohort.training_score)}`}>{cohort.training_score.toFixed(0)}%</span>
                                                    <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                                        <div className={`h-1.5 rounded-full ${getHealthColor(cohort.training_score)}`} style={{ width: `${Math.min(cohort.training_score, 100)}%` }} />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    <span className={`text-sm font-bold ${getHealthTextColor(cohort.yield_score)}`}>{cohort.yield_score.toFixed(0)}%</span>
                                                    <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                                        <div className={`h-1.5 rounded-full ${getHealthColor(cohort.yield_score)}`} style={{ width: `${Math.min(cohort.yield_score, 100)}%` }} />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 pl-4 text-center">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${badge.bg}`}>
                                                    {badge.label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Bottom Row: Activity Feed + Upcoming Tasks ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Activity Feed */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
                            <p className="text-sm text-gray-500">Latest events across your field operations</p>
                        </div>
                    </div>
                    <ActivityFeed />
                </div>

                {/* Upcoming Tasks */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Upcoming Tasks</h2>
                            <p className="text-sm text-gray-500">Your schedule for the next 2 weeks</p>
                        </div>
                        <CalendarIcon className="h-5 w-5 text-gray-400" />
                    </div>

                    {!data || data.upcomingTasks.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <CheckCircleIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p className="font-medium">No upcoming tasks</p>
                            <p className="text-sm text-gray-400 mt-1">Schedule a training session to get started</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {data.upcomingTasks.map((task, index) => (
                                <div
                                    key={index}
                                    className="flex items-start gap-4 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                                >
                                    <div className={`p-2 rounded-lg flex-shrink-0 ${task.type === 'training' ? 'bg-purple-100' : 'bg-green-100'}`}>
                                        {task.type === 'training'
                                            ? <AcademicCapIcon className="h-4 w-4 text-purple-600" />
                                            : <BanknotesIcon className="h-4 w-4 text-green-600" />
                                        }
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-gray-900 text-sm truncate">{task.title}</h4>
                                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                                            <span>{new Date(task.date).toLocaleDateString()}</span>
                                            {task.location && <><span>•</span><span className="truncate">{task.location}</span></>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <button
                        onClick={() => navigate('/dashboard/training')}
                        className="mt-4 w-full py-2 text-sm font-medium text-brand-blue-600 hover:text-brand-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                        View full training calendar →
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FieldFacilitatorDashboard;
